import * as React from "react"
import { cn } from "@/lib/utils"

type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
type ButtonSize = "default" | "sm" | "lg" | "icon" | "icon-sm"

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

const getVariantClasses = (variant: ButtonVariant) => {
  switch (variant) {
    case "default":
      return "bg-primary text-primary-foreground shadow hover:bg-primary/90"
    case "destructive":
      return "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90"
    case "outline":
      return "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground"
    case "secondary":
      return "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80"
    case "ghost":
      return "hover:bg-accent hover:text-accent-foreground"
    case "link":
      return "text-primary underline-offset-4 hover:underline"
    default:
      return "bg-primary text-primary-foreground shadow hover:bg-primary/90"
  }
}

const getSizeClasses = (size: ButtonSize) => {
  switch (size) {
    case "default":
      return "h-10 rounded-xl px-4"
    case "sm":
      return "h-9 rounded-xl px-3 text-[12px]"
    case "lg":
      return "h-11 rounded-xl px-5"
    case "icon":
      return "h-10 w-10 rounded-xl"
    case "icon-sm":
      return "h-8 w-8 rounded-lg"
    default:
      return "h-10 rounded-xl px-4"
  }
}

/**
 * Button component with multiple variants and sizes
 * @param className - Additional CSS classes
 * @param variant - Button variant (default, destructive, outline, secondary, ghost, link)
 * @param size - Button size (default, sm, lg, icon)
 */
/**
 * Button variants configuration for external use
 */
export const buttonVariants = ({ variant = "default", size = "default" }: { variant?: ButtonVariant, size?: ButtonSize } = {}) => {
  return cn(
    "type-control inline-flex items-center justify-center whitespace-nowrap font-semibold transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
    getVariantClasses(variant),
    getSizeClasses(size)
  )
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", ...props }, ref) => {
    return (
      <button
        className={cn(
          buttonVariants({ variant, size }),
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button }
